<template>
    <div class="max-w-8xl mx-auto w-full space-y-4">
        <!-- Page Header -->
        <div class="flex flex-wrap items-center justify-between gap-3">
            <h1 class="text-xl font-semibold text-gray-900 dark:text-white">
                Forums
            </h1>
            <router-link
                v-if="canCreate"
                to="/forums/create"
                class="btn btn-primary"
            >
                <Plus class="w-4 h-4 mr-1" />
                Add Forum
            </router-link>
        </div>

        <!-- Search and Filters -->
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
            <div class="space-y-3">
                <input
                    v-model="searchQuery"
                    @input="debouncedSearch"
                    type="text"
                    placeholder="Search by title..."
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
                />

                <div class="flex flex-wrap gap-3">
                    <SearchableSelect
                        v-model="filters.class_subject_id"
                        :options="classSubjectOptions"
                        placeholder="All Class Subjects"
                        :option-label="getClassSubjectLabel"
                        option-value="id"
                        class="flex-1 min-w-[180px]"
                        @update:model-value="fetchForums"
                    />

                    <SearchableSelect
                        v-model="filters.is_active"
                        :options="statusOptions"
                        placeholder="All Statuses"
                        option-label="label"
                        option-value="value"
                        class="flex-1 min-w-[140px]"
                        @update:model-value="fetchForums"
                    />

                    <button
                        @click="resetFilters"
                        v-if="hasActiveFilters"
                        class="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                    >
                        <RotateCcw class="w-4 h-4" />
                        Reset Filters
                    </button>
                </div>
            </div>
        </div>

        <!-- Loading State -->
        <div
            v-if="loading"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <SkeletonLoader variant="table" :rows="6" :cols="6" />
        </div>

        <!-- Error State -->
        <div
            v-if="error"
            class="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-4"
        >
            <div class="flex">
                <div class="flex-shrink-0">
                    <div class="h-5 w-5 text-danger-400">⚠️</div>
                </div>
                <div class="ml-3">
                    <h3
                        class="text-sm font-medium text-danger-800 dark:text-danger-200"
                    >
                        Error
                    </h3>
                    <p
                        class="mt-1 text-sm text-danger-700 dark:text-danger-300"
                    >
                        {{ error }}
                    </p>
                    <button
                        @click="clearError"
                        class="mt-2 text-sm text-danger-600 dark:text-danger-400 hover:text-danger-500"
                    >
                        Dismiss
                    </button>
                </div>
            </div>
        </div>

        <!-- Data Table -->
        <div
            v-if="!loading && !error"
            class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden"
        >
            <div class="overflow-x-auto">
                <table
                    class="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
                    style="width: 100%"
                >
                    <thead class="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Title
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Class Subject
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Teacher
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Threads
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Rules
                            </th>
                            <th
                                class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider"
                            >
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody
                        class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700"
                    >
                        <tr
                            v-for="forum in forums"
                            :key="forum.id"
                            class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div
                                    class="text-sm font-medium text-gray-900 dark:text-white"
                                >
                                    {{ forum.title }}
                                </div>
                                <div
                                    v-if="forum.description"
                                    class="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs"
                                >
                                    {{ forum.description }}
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ forum.class_subject?.class?.name || "-" }}
                                -
                                {{ forum.class_subject?.subject?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ forum.teacher?.user?.name || "-" }}
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white"
                            >
                                {{ forum.main_post_count || 0 }}
                            </td>
                            <td class="px-6 py-4 whitespace-nowrap">
                                <div class="flex flex-wrap gap-1">
                                    <span
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full"
                                        :class="
                                            forum.is_active
                                                ? 'bg-success-100 text-success-800 dark:bg-success-900 dark:text-success-200'
                                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                                        "
                                    >
                                        {{
                                            forum.is_active
                                                ? "Active"
                                                : "Inactive"
                                        }}
                                    </span>
                                    <span
                                        v-if="forum.require_approval"
                                        class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-warning-100 text-warning-800 dark:bg-warning-900 dark:text-warning-200"
                                    >
                                        Moderated
                                    </span>
                                </div>
                            </td>
                            <td
                                class="px-6 py-4 whitespace-nowrap text-sm font-medium"
                            >
                                <button
                                    @click="openForum(forum)"
                                    title="Open Forum"
                                    class="inline-flex items-center justify-center p-2 text-success-600 dark:text-success-400 hover:text-success-900 dark:hover:text-success-300 rounded-md hover:bg-success-50 dark:hover:bg-success-900/20 transition-colors mr-2"
                                >
                                    <Eye class="w-4 h-4" />
                                </button>
                                <button
                                    v-if="canManage(forum)"
                                    @click="editForum(forum)"
                                    title="Edit Forum"
                                    class="inline-flex items-center justify-center p-2 text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300 rounded-md hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors mr-2"
                                >
                                    <Pencil class="w-4 h-4" />
                                </button>
                                <button
                                    v-if="canManage(forum)"
                                    @click="confirmDelete(forum)"
                                    title="Delete Forum"
                                    class="inline-flex items-center justify-center p-2 text-danger-600 dark:text-danger-400 hover:text-danger-900 dark:hover:text-danger-300 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
                                >
                                    <Trash2 class="w-4 h-4" />
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <EmptyState
                v-if="!loading && forums.length === 0"
                icon="MessagesSquareIcon"
                title="No forums found"
                description="Forums are discussion spaces per class subject."
            />

            <BasePagination
                v-if="forums.length > 0"
                :pagination="meta"
                :per-page="filters.per_page || 15"
                :per-page-options="[10, 15, 25, 50, 100]"
                @page-change="goToPage"
                @per-page-change="handlePerPageChange"
            />
        </div>
    </div>

    <DeleteConfirmationModal
        :show="showDeleteModal"
        title="Delete Forum"
        :message="
            forumToDelete
                ? `Delete forum '${forumToDelete.title}'? All threads will be removed.`
                : ''
        "
        @confirm="deleteForum"
        @close="closeDeleteModal"
    />
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { useRouter } from "vue-router";
import { debounce } from "lodash-es";
import SearchableSelect from "@/components/SearchableSelect.vue";
import BasePagination from "@/components/BasePagination.vue";
import SkeletonLoader from "@/components/SkeletonLoader.vue";
import EmptyState from "@/components/EmptyState.vue";
import DeleteConfirmationModal from "@/components/DeleteConfirmationModal.vue";
import { RotateCcw, Eye, Pencil, Trash2, Plus } from "@lucide/vue";
import forumApi, { Forum, ForumFilters } from "@/api/forum";
import { classSubjectApi } from "@/api/classSubject";
import { useAuthStore } from "@/stores/authStore";

const router = useRouter();
const authStore = useAuthStore();

const forums = ref<Forum[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const meta = ref<any>(null);

const searchQuery = ref("");
const filters = ref<ForumFilters>({
    class_subject_id: null,
    teacher_id: null,
    is_active: null,
    per_page: 15,
});

const classSubjectOptions = ref<any[]>([]);

const statusOptions = [
    { label: "Active", value: true },
    { label: "Inactive", value: false },
];

const canCreate = computed(() =>
    authStore.hasAnyRole(["administrator", "super-administrator", "teacher"]),
);

const hasActiveFilters = computed(() => {
    const { class_subject_id, is_active } = filters.value;
    return (
        searchQuery.value.trim() !== "" ||
        (class_subject_id !== null && class_subject_id !== undefined) ||
        (is_active !== null && is_active !== undefined)
    );
});

const debouncedSearch = debounce(() => {
    fetchForums();
}, 500);

async function fetchForums() {
    loading.value = true;
    error.value = null;

    try {
        const response = await forumApi.getAll({
            ...filters.value,
            search: searchQuery.value || undefined,
        });

        forums.value = response.data.items;
        meta.value = response.data.meta;
    } catch (err: any) {
        error.value = err.response?.data?.message || "Failed to fetch forums";
    } finally {
        loading.value = false;
    }
}

function resetFilters() {
    filters.value = {
        class_subject_id: null,
        teacher_id: null,
        is_active: null,
        per_page: 15,
    };
    searchQuery.value = "";
    fetchForums();
}

function clearError() {
    error.value = null;
}

async function fetchClassSubjects() {
    try {
        const response = await classSubjectApi.getAll({ per_page: 1000 });
        classSubjectOptions.value = response.data.items || [];
    } catch {
        classSubjectOptions.value = [];
    }
}

function getClassSubjectLabel(option: any) {
    return `${option.class?.grade_level || ""} ${option.class?.name || ""} - ${option.subject?.name || ""} (${option.semester?.name || "-"})`;
}

function canManage(forum: Forum) {
    return (
        authStore.hasAnyRole(["administrator", "super-administrator"]) ||
        authStore.user?.id === forum.teacher?.user?.id
    );
}

function openForum(forum: Forum) {
    router.push(`/forums/${forum.id}`);
}

const forumToDelete = ref<Forum | null>(null);

function editForum(forum: Forum) {
    router.push(`/forums/${forum.id}/edit`);
}

const showDeleteModal = ref(false);

function confirmDelete(forum: Forum) {
    forumToDelete.value = forum;
    showDeleteModal.value = true;
}

function closeDeleteModal() {
    showDeleteModal.value = false;
    forumToDelete.value = null;
}

async function deleteForum() {
    if (!forumToDelete.value) return;

    try {
        await forumApi.delete(forumToDelete.value.id);
        await fetchForums();
    } catch (err: any) {
        error.value = err.response?.data?.message || "Failed to delete forum";
    } finally {
        closeDeleteModal();
    }
}

function goToPage(page: number) {
    if (meta.value) {
        filters.value.page = page;
        fetchForums();
    }
}

function handlePerPageChange(value: number) {
    filters.value.per_page = value;
    filters.value.page = 1;
    fetchForums();
}

onMounted(() => {
    fetchForums();
    fetchClassSubjects();
});
</script>
